import { useEffect, useRef, useState, SubmitEvent, ReactElement } from "react";
import axios from "axios";
import { t } from "@lingui/core/macro";
import { Link, useLocation } from "_/components/router";
import { useAtom } from "jotai";

import { useMailEnabled } from "_/data/server";
import { useLoginUser, useCheckUser, useSsoCheck } from "_/data/users";
import { useSetLocationAndOrg } from "_/hooks/useSetLocationAndOrg";
import {
  lastLoginEmailAtom,
  lastLoginMethodAtom,
  nextUrlAtom,
  useTargetOrg,
} from "_/state";
import { publicUrls, routeUrls } from "_/routes";
import { Checkbox } from "_/components/checkbox";
import { TextField } from "_/components/text-field";

import * as S from "./styled";

type LoginStep = "password" | "redirect";

const LoadingAnimation = (
  <S.LoadingDots>
    {[0, 1, 2, 3].map((i) => (
      <S.Dot key={i} $delay={i * 0.12} />
    ))}
  </S.LoadingDots>
);

const SSO_CHECK_DEBOUNCE_MS = 250;

export const Login = (): ReactElement => {
  const [_location, setLocation] = useLocation();
  const [nextUrl, setNextUrl] = useAtom(nextUrlAtom);
  const [targetOrg, setTargetOrg] = useTargetOrg();
  const setLocationAndOrg = useSetLocationAndOrg();

  const { data: user } = useCheckUser();
  const loginUser = useLoginUser();
  const { mutateAsync: checkSsoForEmail } = useSsoCheck();

  // Password recovery is emailed, so hide it on installs without email.
  const showForgotPassword = useMailEnabled() !== false;

  const [lastLoginEmail, setLastLoginEmail] = useAtom(lastLoginEmailAtom);
  const [lastLoginMethod, setLastLoginMethod] = useAtom(lastLoginMethodAtom);

  const [email, setEmail] = useState(lastLoginEmail);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const initialStep: LoginStep =
    lastLoginMethod === "sso" && lastLoginEmail ? "redirect" : "password";
  const [step, setStep] = useState<LoginStep>(initialStep);
  const [checking, setChecking] = useState(initialStep === "redirect");
  const [errorMessage, setErrorMessage] = useState("");
  const [redirecting, setRedirecting] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Store the SSO redirect URL when detected.
  const ssoRedirectUrl = useRef<string | undefined>(undefined);

  // Email present at pageload — used to keep the password field enabled
  // during the initial SSO recheck so a returning user keeps their focus.
  const initialEmail = useRef(email);

  // Login method cached at pageload. Held in a ref so that persisting a
  // successful login doesn't re-run the initial-focus effect below.
  const initialMethod = useRef(lastLoginMethod);

  const loggedIn = !!user;
  const isAuthenticating = loginUser.isPending || redirecting;

  // Disabled during checks only when the email differs from pageload, so
  // the cached-email recheck doesn't steal focus from a returning user.
  const passwordDisabled =
    isAuthenticating || (checking && email !== initialEmail.current);

  // Redirect if already logged in, replacing history so the back button
  // doesn't return to the login page.
  useEffect(() => {
    if (loggedIn) {
      const url = nextUrl || routeUrls.index;
      if (targetOrg) {
        setLocationAndOrg({ orgId: targetOrg, url, replace: true });
        setTargetOrg(null);
      } else {
        setLocation(url, { replace: true });
      }
      setNextUrl(null);
    }
  }, [
    loggedIn,
    nextUrl,
    setNextUrl,
    targetOrg,
    setTargetOrg,
    setLocation,
    setLocationAndOrg,
  ]);

  // Initial focus: password if returning with a cached password method,
  // otherwise email if it's empty.
  useEffect(() => {
    if (initialMethod.current === "password" && initialEmail.current) {
      passwordRef.current?.focus();
    } else if (!initialEmail.current) {
      emailRef.current?.focus();
    }
  }, []);

  // Debounced SSO detection on email change.
  useEffect(() => {
    if (!email) {
      setStep("password");
      setChecking(false);
      ssoRedirectUrl.current = undefined;
      return undefined;
    }

    setChecking(true);

    const checkSso = async () => {
      try {
        const response = await checkSsoForEmail({ email });
        if (response.method !== "password" && response.redirectUrl) {
          setStep("redirect");
          ssoRedirectUrl.current = response.redirectUrl;
        } else {
          setStep("password");
          ssoRedirectUrl.current = undefined;
        }
      } catch {
        // If the check fails, default to password.
        setStep("password");
        ssoRedirectUrl.current = undefined;
      } finally {
        setChecking(false);
      }
    };

    const timer = setTimeout(() => void checkSso(), SSO_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [email, checkSsoForEmail]);

  function persistLogin(method: "password" | "sso") {
    if (remember) {
      setLastLoginEmail(email);
      setLastLoginMethod(method);
    } else {
      setLastLoginEmail("");
      setLastLoginMethod("");
    }
  }

  async function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMessage("");

    if (!email || (!password && step === "password")) {
      setErrorMessage(t`components.login.empty-fields`);
      return;
    }

    if (step === "redirect") {
      // SSO flow.
      try {
        // Re-check to get a fresh redirect URL, passing nextUrl so the server
        // can redirect back to the originally requested page after auth.
        const response = await checkSsoForEmail({
          email,
          redirectTo: nextUrl ?? undefined,
        });

        if (response.redirectUrl) {
          persistLogin("sso");
          setRedirecting(true);

          // Clear nextUrl before navigating away — the IdP server will handle the
          // redirect to the target URL, so the client-side atom is no longer needed.
          setNextUrl(null);
          window.location.replace(response.redirectUrl);

          return;
        }

        setErrorMessage(t`components.login.login-error`);
      } catch (err) {
        if (!axios.isAxiosError(err)) {
          throw err;
        }

        setErrorMessage(t`components.login.login-error`);
      }
      return;
    }

    // Password flow.
    try {
      persistLogin("password");
      await loginUser.mutateAsync({ email, password });
    } catch (err) {
      if (!axios.isAxiosError(err)) throw err;
      if (err.response?.status !== 401) throw err;
      setErrorMessage(t`components.login.login-error`);
    }
  }

  if (redirecting) {
    return (
      <S.Wrapper>
        <S.RedirectNotice>
          {LoadingAnimation}
          <S.RedirectText>{t`components.login.sso-redirect`}</S.RedirectText>
        </S.RedirectNotice>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      <S.Title>{t`components.login.title`}</S.Title>

      <S.LoginForm onSubmit={(e) => void handleSubmit(e)}>
        <TextField
          ref={emailRef}
          type="email"
          label={t`components.login.email-label`}
          placeholder={t`components.login.email-placeholder`}
          defaultValue={email}
          onChange={(v: string) => setEmail(v ?? "")}
          autoComplete="username"
          iconRight={checking ? <S.CheckingSpinner /> : undefined}
        />

        <S.CollapsibleField $visible={step === "password"}>
          <TextField
            ref={passwordRef}
            type="password"
            label={t`components.login.password-label`}
            placeholder={t`components.login.password-placeholder`}
            onChange={(v: string) => setPassword(v ?? "")}
            autoComplete="current-password"
            disabled={passwordDisabled || step !== "password"}
            tabIndex={step === "password" ? 0 : -1}
          />
        </S.CollapsibleField>

        <S.OptionsRow>
          <Checkbox
            id="login-remember"
            checked={remember}
            onChange={setRemember}
            label={t`components.login.remember`}
          />
          {showForgotPassword && (
            <Link to={publicUrls.recovery}>
              <S.ForgotLink as="span">
                {t`components.login.forgot-password`}
              </S.ForgotLink>
            </Link>
          )}
        </S.OptionsRow>

        {errorMessage && (
          <S.ErrorLine>
            <S.ErrorBadge>!</S.ErrorBadge>
            {errorMessage}
          </S.ErrorLine>
        )}

        <div style={{ marginTop: 4 }}>
          <S.LoginButton
            type="submit"
            size="medium"
            kind="primary"
            disabled={isAuthenticating || checking}
          >
            {isAuthenticating
              ? LoadingAnimation
              : step === "redirect"
                ? t`components.login.sso-button`
                : t`components.login.login-button`}
          </S.LoginButton>
        </div>
      </S.LoginForm>

      <S.Footer>{t`components.login.footer`}</S.Footer>
    </S.Wrapper>
  );
};
